import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Badge,
  Input,
  Label,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import config from "../../config/config";
import "./gallery.scss";

function GalleryList() {
  const navigate = useNavigate();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const breadcrumbItems = [
    { title: "Gallery", link: "#" },
    { title: "Gallery List", link: "#" },
  ];

  const defaultCategories = ["Leisure", "Spiritual", "Experiential", "MICE", "Educational"];

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http") || photo.startsWith("data:")) return photo;
    const baseUrl = config.API_BASE_URL.replace(/\/api\/?$/, "");
    return photo.startsWith("/") ? `${baseUrl}${photo}` : `${baseUrl}/uploads/${photo}`;
  };

  const fetchGalleryItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/gallery`, {
        headers: { "ngrok-skip-browser-warning": "true" },
      });
      if (response.ok) {
        const data = await response.json();
        setGalleryItems(
          Array.isArray(data)
            ? data
            : data.gallery || data.data || data.items || []
        );
      } else {
        setGalleryItems([]);
      }
    } catch (err) {
      console.error("Error fetching gallery items:", err);
      setError(err.message);
      setGalleryItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    navigate(`/edit-gallery/${item._id || item.id}`, {
      state: { galleryItem: item },
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this gallery item?")) {
      try {
        const response = await fetch(`${config.API_BASE_URL}/gallery/${id}`, {
          method: "DELETE",
          headers: { "ngrok-skip-browser-warning": "true" },
        });
        if (response.ok) {
          setGalleryItems(galleryItems.filter((g) => (g._id || g.id) !== id));
        } else {
          console.error("Failed to delete gallery item");
        }
      } catch (err) {
        console.error("Error deleting gallery item:", err);
      }
    }
  };

  const getCategoryBadgeColor = (category) => {
    switch (category) {
      case "Leisure":
        return "info";
      case "Spiritual":
        return "warning";
      case "Experiential":
        return "success";
      case "MICE":
        return "primary";
      case "Educational":
        return "secondary";
      default:
        return "primary";
    }
  };

  const categoriesList = Array.from(
    new Set([
      ...defaultCategories,
      ...galleryItems
        .map((item) => item.tour_category || item.tourCategory || item.category)
        .filter(Boolean),
    ])
  );

  const filteredGalleryItems = galleryItems.filter((item) => {
    if (selectedCategory === "All" || !selectedCategory) return true;
    const tourCategory = item.tour_category || item.tourCategory || item.category || "Leisure";
    return tourCategory.toLowerCase() === selectedCategory.toLowerCase();
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Gallery List"
            breadcrumbItems={breadcrumbItems}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <Label htmlFor="categoryFilterSelect" className="mb-0 fw-semibold text-muted me-1">
                        Filter by Tour Category:
                      </Label>
                      <Input
                        type="select"
                        id="categoryFilterSelect"
                        className="form-select style-select"
                        style={{ minWidth: "200px", width: "auto" }}
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        <option value="All">All Categories ({galleryItems.length})</option>
                        {categoriesList.map((cat) => {
                          const count = galleryItems.filter(
                            (g) =>
                              (g.tour_category || g.tourCategory || g.category || "Leisure").toLowerCase() ===
                              cat.toLowerCase()
                          ).length;
                          return (
                            <option key={cat} value={cat}>
                              {cat} ({count})
                            </option>
                          );
                        })}
                      </Input>

                      {selectedCategory !== "All" && (
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => setSelectedCategory("All")}
                          title="Clear filter"
                          className="ms-1"
                        >
                          <i className="ri-close-line align-middle me-1"></i> Clear Filter
                        </Button>
                      )}
                    </div>

                    <div>
                      <Link to="/create-gallery">
                        <Button color="primary">
                          <i className="ri-add-line align-middle me-1"></i> Add Gallery Item
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">Loading gallery items...</div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-centered table-nowrap mb-0">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Photo</th>
                            <th>Title</th>
                            <th>Tour Category</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredGalleryItems.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                {selectedCategory !== "All" ? (
                                  <div>
                                    <p className="mb-2 text-muted">
                                      No gallery items found under "<strong>{selectedCategory}</strong>" category.
                                    </p>
                                    <Button
                                      color="link"
                                      size="sm"
                                      onClick={() => setSelectedCategory("All")}
                                    >
                                      Show all gallery items
                                    </Button>
                                  </div>
                                ) : (
                                  "No gallery items found."
                                )}
                              </td>
                            </tr>
                          ) : (
                            filteredGalleryItems.map((item, index) => {
                              const photoUrl = getPhotoUrl(item.photo || item.image);
                              const tourCategory =
                                item.tour_category || item.tourCategory || item.category || "Leisure";
                              return (
                                <tr key={item._id || item.id || index}>
                                  <td>{item._id || item.id || index + 1}</td>
                                  <td>
                                    {photoUrl ? (
                                      <img
                                        src={photoUrl}
                                        alt={item.title || "Gallery Item"}
                                        className="gallery-thumbnail"
                                      />
                                    ) : (
                                      <div className="avatar-xs">
                                        <span className="avatar-title rounded bg-primary-subtle text-primary fw-bold">
                                          G
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="fw-semibold">{item.title || "N/A"}</td>
                                  <td>
                                    <Badge
                                      color={getCategoryBadgeColor(tourCategory)}
                                      className="gallery-category-badge"
                                    >
                                      {tourCategory}
                                    </Badge>
                                  </td>
                                  <td>
                                    <button
                                      className="gallery-action-buttons me-2 text-primary"
                                      onClick={() => handleEdit(item)}
                                      title="Edit"
                                    >
                                      <i className="ri-pencil-fill"></i>
                                    </button>
                                    <button
                                      className="gallery-action-buttons text-danger"
                                      onClick={() => handleDelete(item._id || item.id)}
                                      title="Delete"
                                    >
                                      <i className="ri-delete-bin-fill"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default GalleryList;

